import React from 'react';
import { Dimensions, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { useNavigate } from 'react-router-dom';
import { toHtmlTextStyle } from '../components/general/toHtmlTextStyle';

const WIDTH = Dimensions.get('window').width;
const PAGE_HORIZONTAL_PADDING = WIDTH >= 1200 ? 100 : 24;

const ownerSteps = [
    {
        icon: 'storefront-outline',
        title: 'Build your camp profile',
        description: 'Showcase your programs, ages, schedules, location, and amenities so families can quickly understand what makes your camp stand out.',
    },
    {
        icon: 'calendar-check-outline',
        title: 'Manage sessions and registrations',
        description: 'Publish seasonal sessions, track active enrollments, monitor availability, and keep registration details organized in one place.',
    },
    {
        icon: 'account-group-outline',
        title: 'Stay connected with families',
        description: 'Share updates, answer parent questions, and keep communication streamlined as families move from discovery to enrollment.',
    },
    {
        icon: 'chart-line',
        title: 'Grow with better visibility',
        description: 'Reach more families searching for kids summer camps and turn your SnapCamp listing into a year-round growth channel.',
    },
];

const parentSteps = [
    {
        icon: 'magnify',
        title: 'Search nearby camps',
        description: 'Explore kids summer camps by location, interests, and availability to quickly find programs that match your child and your schedule.',
    },
    {
        icon: 'map-marker-radius-outline',
        title: 'Compare trusted options',
        description: 'Review camp details, browse photos, compare categories, and understand what each program offers before making a decision.',
    },
    {
        icon: 'clipboard-check-outline',
        title: 'Track registrations with confidence',
        description: 'Keep important camp details, session options, and next steps easy to review while choosing the best fit for your family.',
    },
    {
        icon: 'message-text-fast-outline',
        title: 'Connect with camp organizers',
        description: 'Reach out directly when you need answers about schedules, availability, logistics, or the overall camp experience.',
    },
];

const quickLinks = [
    { icon: 'campfire', label: 'Camp owners: create and manage your camp listing' },
    { icon: 'account-child-outline', label: 'Parents: discover camps and compare programs' },
    { icon: 'calendar-month-outline', label: 'Review sessions, availability, and registration details' },
    { icon: 'shield-check-outline', label: 'Make decisions with clear, organized camp information' },
];

const StepCard = ({ icon, title, description, tint }: { icon: React.ComponentProps<typeof MaterialCommunityIcons>['name']; title: string; description: string; tint: string; }) => (
    <View style={styles.stepCard}>
        <View style={[styles.stepIconWrap, { backgroundColor: tint }]}>
            <MaterialCommunityIcons name={icon} size={24} color="#0F172A" />
        </View>
        <Text style={styles.stepTitle}>{title}</Text>
        <Text style={styles.stepDescription}>{description}</Text>
    </View>
);

const AudienceSection = ({
    eyebrow,
    title,
    description,
    steps,
    accent,
    sectionIcon,
    ctaLabel,
    ctaRoute,
}: {
    eyebrow: string;
    title: string;
    description: string;
    steps: typeof ownerSteps;
    accent: string;
    sectionIcon: React.ComponentProps<typeof MaterialCommunityIcons>['name'];
    ctaLabel: string;
    ctaRoute: string;
}) => {
    const navigate = useNavigate();

    return (
        <View style={styles.sectionCard}>
            <View style={styles.sectionHero}>
                <View style={styles.sectionCopy}>
                    <View style={[styles.sectionIconWrap, { backgroundColor: `${accent}18` }]}>
                        <MaterialCommunityIcons name={sectionIcon} size={28} color={accent} />
                    </View>
                    <View style={styles.sectionHeader}>
                        <Text style={[styles.eyebrow, { color: accent }]}>{eyebrow}</Text>
                        <Text style={styles.sectionTitle}>{title}</Text>
                        <Text style={styles.sectionDescription}>{description}</Text>
                    </View>
                </View>
                <Pressable
                    style={[styles.inlineCtaButton, { backgroundColor: accent }]}
                    onPress={() => navigate(ctaRoute)}
                >
                    <Text style={styles.inlineCtaText}>{ctaLabel}</Text>
                    <MaterialCommunityIcons name="arrow-right" size={18} color="#FFFFFF" />
                </Pressable>
            </View>

            <View style={styles.stepsGrid}>
                {steps.map((step) => (
                    <StepCard key={step.title} {...step} tint={`${accent}22`} />
                ))}
            </View>
        </View>
    );
};

const HelpCenterScreen = () => {
    return (
        <ScrollView style={styles.page} contentContainerStyle={styles.pageContent}>
            <View style={styles.container}>
                <View style={styles.hero}>
                    <View style={styles.heroBadge}>
                        <MaterialCommunityIcons name="lifebuoy" size={16} color="#1D4ED8" />
                        <Text style={styles.heroBadgeText}>Help Center</Text>
                    </View>

                    <h1 style={toHtmlTextStyle(styles.heroTitle, { marginTop: 0 })}>Everything parents and camp owners need to use SnapCamp with confidence.</h1>
                    <Text style={styles.heroSubtitle}>
                        SnapCamp is a marketplace for kids summer camps, built to help families discover the right programs and help camp owners manage listings, registrations, and growth.
                    </Text>

                    <View style={styles.quickLinksGrid}>
                        {quickLinks.map((item) => (
                            <View key={item.label} style={styles.quickLinkCard}>
                                <MaterialCommunityIcons name={item.icon as any} size={20} color="#1D4ED8" />
                                <Text style={styles.quickLinkText}>{item.label}</Text>
                            </View>
                        ))}
                    </View>
                </View>

                <AudienceSection
                    eyebrow="For camp owners"
                    title="For camp owners: grow your camp and manage registrations"
                    description="Use SnapCamp to present your camp professionally, organize session details, and make it easier for families to discover and trust your programs."
                    steps={ownerSteps}
                    accent="#2563EB"
                    sectionIcon="storefront-outline"
                    ctaLabel="Start listing your camps"
                    ctaRoute="/hosting-create-camp"
                />

                <AudienceSection
                    eyebrow="For parents"
                    title="For parents: find and compare the right summer camp"
                    description="Use SnapCamp to browse programs, evaluate options more easily, and stay organized while choosing the best camp for your child."
                    steps={parentSteps}
                    accent="#059669"
                    sectionIcon="account-child-outline"
                    ctaLabel="Explore camps near you"
                    ctaRoute="/explore"
                />
            </View>
        </ScrollView>
    );
};

const styles = StyleSheet.create({
    page: {
        flex: 1,
        backgroundColor: '#F8FAFC',
    },
    pageContent: {
        paddingTop: 36,
        paddingBottom: 72,
        paddingHorizontal: PAGE_HORIZONTAL_PADDING,
    },
    container: {
        width: '100%',
        gap: 28,
    },
    hero: {
        backgroundColor: '#FFFFFF',
        borderRadius: 28,
        padding: 32,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        shadowColor: '#0F172A',
        shadowOpacity: 0.06,
        shadowRadius: 18,
        shadowOffset: { width: 0, height: 8 },
    },
    heroBadge: {
        alignSelf: 'flex-start',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 999,
        backgroundColor: '#DBEAFE',
        marginBottom: 18,
    },
    heroBadgeText: {
        fontSize: 13,
        fontWeight: '700',
        color: '#1D4ED8',
    },
    heroTitle: {
        fontSize: 36,
        lineHeight: 44,
        fontWeight: '800',
        color: '#0F172A',
        marginBottom: 14,
    },
    heroSubtitle: {
        fontSize: 17,
        lineHeight: 28,
        color: '#475569',
        marginBottom: 24,
    },
    quickLinksGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 14,
        justifyContent: 'space-between',
    },
    quickLinkCard: {
        flexBasis: 0,
        flexGrow: 1,
        minWidth: 280,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        backgroundColor: '#F8FAFC',
        borderWidth: 1,
        borderColor: '#E2E8F0',
        borderRadius: 18,
        paddingHorizontal: 16,
        paddingVertical: 14,
    },
    quickLinkText: {
        flex: 1,
        fontSize: 15,
        lineHeight: 22,
        color: '#1E293B',
        fontWeight: '600',
    },
    sectionCard: {
        backgroundColor: '#FFFFFF',
        borderRadius: 28,
        padding: 32,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        shadowColor: '#0F172A',
        shadowOpacity: 0.05,
        shadowRadius: 16,
        shadowOffset: { width: 0, height: 6 },
    },
    sectionHero: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 20,
        marginBottom: 22,
        flexWrap: 'wrap',
    },
    sectionCopy: {
        flex: 1,
        minWidth: 320,
    },
    sectionIconWrap: {
        width: 58,
        height: 58,
        borderRadius: 18,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 16,
    },
    sectionHeader: {
        maxWidth: 860,
    },
    eyebrow: {
        fontSize: 13,
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: 1,
        marginBottom: 8,
    },
    sectionTitle: {
        fontSize: 34,
        lineHeight: 42,
        fontWeight: '800',
        color: '#0F172A',
        marginBottom: 10,
    },
    sectionDescription: {
        fontSize: 16,
        lineHeight: 26,
        color: '#475569',
    },
    inlineCtaButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingHorizontal: 18,
        paddingVertical: 14,
        borderRadius: 999,
        shadowColor: '#0F172A',
        shadowOpacity: 0.12,
        shadowRadius: 10,
        shadowOffset: { width: 0, height: 6 },
    },
    inlineCtaText: {
        color: '#FFFFFF',
        fontSize: 15,
        fontWeight: '700',
    },
    stepsGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 16,
        justifyContent: 'space-between',
    },
    stepCard: {
        flexBasis: 0,
        flexGrow: 1,
        minWidth: 280,
        backgroundColor: '#F8FAFC',
        borderWidth: 1,
        borderColor: '#E2E8F0',
        borderRadius: 20,
        padding: 20,
    },
    stepIconWrap: {
        width: 48,
        height: 48,
        borderRadius: 14,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 14,
    },
    stepTitle: {
        fontSize: 18,
        lineHeight: 24,
        fontWeight: '700',
        color: '#0F172A',
        marginBottom: 8,
    },
    stepDescription: {
        fontSize: 15,
        lineHeight: 24,
        color: '#475569',
    },
});

export default HelpCenterScreen;
